import { useCallback, useEffect, useState, PropsWithChildren } from 'react';
import { TextField } from '../../../common/components/fields/TextField/TextField';
import { useChangeEvent } from '../../../common/hooks/useChangeEvent';
import { AutoCompleteBody } from './AutoCompleteBody';
import { Wrapper } from './LocationBox.styles';
import { SuburbType } from './SuburbType';

export interface LBProps {
  labelKey?: string;
  placeholder?: string;
  required?: boolean;
  complete?: boolean;
  error?: boolean;
  className?: string;
  value: string;
  name?: string;
  onSelection?: Setter<SuburbType>;
}

export type LocationBoxProps = PropsWithChildren<LBProps>;

export const LocationBox = ({
  children,
  className,
  value: initialValue,
  onSelection,
  ...textFieldProps
}: LocationBoxProps) => {
  const [focused, setFocused] = useState(false);

  const [focusKey, setFocusKey] = useState(1);

  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = useChangeEvent(input => {
    setValue(input);
  });

  const selectSuburb = useCallback(
    (suburb: SuburbType) => {
      setValue(suburb.name);
      onSelection?.(suburb);
      handleBlur();
    },
    [onSelection],
  );

  const handleFocus = useCallback(() => setFocused(true), []);

  const handleBlur = () => {
    setFocused(false);
    setFocusKey(key => key + 1);
  };

  return (
    <Wrapper className={className}>
      <TextField
        placeholder="Enter your postcode or suburb"
        key={focusKey}
        onChange={handleChange}
        value={value}
        autoComplete="off"
        onFocus={handleFocus}
        onBlur={handleBlur}
        name={textFieldProps.name || 'suburb'}
        {...textFieldProps}
      />

      {focused && value && (
        <AutoCompleteBody query={value} onSelect={selectSuburb} cancel={handleBlur} />
      )}
      {children}
    </Wrapper>
  );
};
